import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
    Container,
    Paper,
    Typography,
    Button,
    Box,
    Divider,
    Grid,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip
} from "@material-ui/core";
import {
    GetApp,
    ExpandMore,
    Code,
    CheckCircle,
    Warning
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(4),
    },
    paper: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    title: {
        marginBottom: theme.spacing(2),
        fontWeight: 600,
    },
    subtitle: {
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(3),
    },
    downloadCard: {
        textAlign: "center",
        padding: theme.spacing(3),
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "white",
        marginBottom: theme.spacing(3),
    },
    downloadButton: {
        marginTop: theme.spacing(2),
        backgroundColor: "white",
        color: "#667eea",
        "&:hover": {
            backgroundColor: "#f5f5f5",
        },
    },
    codeBlock: {
        backgroundColor: "#f5f5f5",
        padding: theme.spacing(2),
        borderRadius: 4,
        fontFamily: "monospace",
        fontSize: 13,
        overflowX: "auto",
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
        whiteSpace: "pre-wrap",
    },
    contactBox: {
        backgroundColor: "#f0f4ff",
        padding: theme.spacing(2),
        borderRadius: 4,
        marginTop: theme.spacing(3),
    },
    featureChip: {
        margin: theme.spacing(0.5),
    },
    warningBox: {
        backgroundColor: "#fff3e0",
        padding: theme.spacing(2),
        borderRadius: 4,
        marginBottom: theme.spacing(2),
        display: "flex",
        alignItems: "flex-start",
    },
    stepCard: {
        height: "100%",
    },
    stepNumber: {
        width: 40,
        height: 40,
        borderRadius: "50%",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        marginBottom: theme.spacing(2),
    },
}));

const DzapyChatIntegration = () => {
    const classes = useStyles();

    const handleDownload = (filename) => {
        const link = document.createElement('a');
        link.href = `/downloads/${filename}`;
        link.download = filename;
        link.click();
    };

    return (
        <div className={classes.root}>
            <Container maxWidth="lg">
                {/* Header */}
                <Paper className={classes.paper}>
                    <Typography variant="h4" className={classes.title}>
                        💬 Dzapy-chat - Rastreamento para Sites Externos
                    </Typography>
                    <Typography variant="body1" className={classes.subtitle}>
                        Script JavaScript para rastrear leads em qualquer site (HTML, WordPress sem plugin, React, etc.)
                    </Typography>
                    <Divider />
                </Paper>

                {/* Downloads */}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card className={classes.downloadCard}>
                            <CardContent>
                                <Code style={{ fontSize: 48, marginBottom: 16 }} />
                                <Typography variant="h6" gutterBottom>
                                    dzapy-tracking.js
                                </Typography>
                                <Typography variant="body2" style={{ marginBottom: 16 }}>
                                    Script de rastreamento
                                </Typography>
                                <Button
                                    variant="contained"
                                    className={classes.downloadButton}
                                    startIcon={<GetApp />}
                                    onClick={() => handleDownload('dzapy-tracking.js')}
                                >
                                    Baixar Script
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card className={classes.downloadCard}>
                            <CardContent>
                                <Code style={{ fontSize: 48, marginBottom: 16 }} />
                                <Typography variant="h6" gutterBottom>
                                    exemplo-tracking.html
                                </Typography>
                                <Typography variant="body2" style={{ marginBottom: 16 }}>
                                    Página de exemplo
                                </Typography>
                                <Button
                                    variant="contained"
                                    className={classes.downloadButton}
                                    startIcon={<GetApp />}
                                    onClick={() => handleDownload('exemplo-tracking.html')}
                                >
                                    Baixar Exemplo
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Features */}
                <Paper className={classes.paper}>
                    <Typography variant="h6" gutterBottom>
                        ✨ O Que é Rastreado
                    </Typography>
                    <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>UTMs Completas:</Typography>
                        <Chip label="utm_source" className={classes.featureChip} size="small" />
                        <Chip label="utm_medium" className={classes.featureChip} size="small" />
                        <Chip label="utm_campaign" className={classes.featureChip} size="small" />
                        <Chip label="utm_content" className={classes.featureChip} size="small" />
                        <Chip label="utm_term" className={classes.featureChip} size="small" />
                    </Box>
                    <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>Anúncios:</Typography>
                        <Chip label="gclid (Google)" className={classes.featureChip} size="small" color="primary" />
                        <Chip label="gad_source" className={classes.featureChip} size="small" color="primary" />
                        <Chip label="gad_campaignid" className={classes.featureChip} size="small" color="primary" />
                        <Chip label="fbclid (Facebook)" className={classes.featureChip} size="small" color="secondary" />
                    </Box>
                    <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>Extras:</Typography>
                        <Chip label="referrer" className={classes.featureChip} size="small" />
                        <Chip label="landing_url" className={classes.featureChip} size="small" />
                        <Chip label="user_agent" className={classes.featureChip} size="small" />
                    </Box>
                </Paper>

                {/* Installation */}
                <Typography variant="h5" gutterBottom style={{ marginTop: 32, marginBottom: 16 }}>
                    📋 Instalação Básica
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Card className={classes.stepCard}>
                            <CardContent>
                                <div className={classes.stepNumber}>1</div>
                                <Typography variant="h6" gutterBottom>
                                    Hospedar Script
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Faça upload do dzapy-tracking.js para seu servidor OU use o CDN hospedado
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card className={classes.stepCard}>
                            <CardContent>
                                <div className={classes.stepNumber}>2</div>
                                <Typography variant="h6" gutterBottom>
                                    Adicionar no Site
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Inclua o script antes do {`</body>`}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card className={classes.stepCard}>
                            <CardContent>
                                <div className={classes.stepNumber}>3</div>
                                <Typography variant="h6" gutterBottom>
                                    Marcar Formulários
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Adicione data-dzapy="true" nos formulários
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Code Examples */}
                <Paper className={classes.paper} style={{ marginTop: 32 }}>
                    <Typography variant="h6" gutterBottom>
                        💻 Código de Instalação
                    </Typography>

                    <Box mb={3}>
                        <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
                            <strong>Opção 1: Usando CDN Hospedado (Recomendado)</strong>
                        </Typography>
                        <Typography variant="body2" color="textSecondary" paragraph>
                            Não precisa fazer download! Use o script hospedado diretamente:
                        </Typography>
                        <div className={classes.codeBlock}>
                            {`<!-- Dzapy Tracking via CDN -->
<script src="https://dzapy.com.br/assets/js/dzapy-tracking.js"></script>
<script>
  DzapyTracking.init({
    apiUrl: 'https://api6.zapatendimento.com.br',
    landingPageId: 123,  // ID da landing page no CRM
    debug: false
  });
</script>`}
                        </div>
                    </Box>

                    <Box mb={3}>
                        <Typography variant="subtitle2" gutterBottom>
                            <strong>Opção 2: Hospedando Você Mesmo</strong>
                        </Typography>
                        <Typography variant="body2" color="textSecondary" paragraph>
                            Faça download do script e hospede em seu servidor:
                        </Typography>
                        <div className={classes.codeBlock}>
                            {`<!-- Dzapy Tracking Local -->
<script src="/js/dzapy-tracking.js"></script>
<script>
  DzapyTracking.init({
    apiUrl: 'https://api6.zapatendimento.com.br',
    landingPageId: 123,
    debug: false
  });
</script>`}
                        </div>
                    </Box>

                    <Typography variant="subtitle2" gutterBottom style={{ marginTop: 24 }}>
                        Marque seus formulários:
                    </Typography>
                    <div className={classes.codeBlock}>
                        {`<!-- Opção 1: Usando data attribute -->
<form data-dzapy="true">
  <input type="text" name="name" placeholder="Nome" required>
  <input type="tel" name="phone" placeholder="WhatsApp" required>
  <input type="email" name="email" placeholder="Email">
  <button type="submit">Enviar</button>
</form>

<!-- Opção 2: Usando classe -->
<form class="dzapy-form">
  <input type="text" name="nome" placeholder="Nome" required>
  <input type="tel" name="telefone" placeholder="Telefone" required>
  <button type="submit">Enviar</button>
</form>`}
                    </div>
                </Paper>

                {/* Field Names */}
                <Paper className={classes.paper}>
                    <Typography variant="h6" gutterBottom>
                        📝 Nomes de Campos Aceitos
                    </Typography>
                    <Typography variant="body2" paragraph>
                        O script reconhece automaticamente diferentes nomes:
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="subtitle2">Nome:</Typography>
                            <Typography variant="body2" color="textSecondary">
                                name, nome, full_name
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="subtitle2">Telefone:</Typography>
                            <Typography variant="body2" color="textSecondary">
                                phone, telefone, whatsapp, celular
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="subtitle2">Email:</Typography>
                            <Typography variant="body2" color="textSecondary">
                                email, e-mail
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Advanced Examples */}
                <Typography variant="h5" gutterBottom style={{ marginTop: 32, marginBottom: 16 }}>
                    🎨 Exemplos Avançados
                </Typography>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography>Com Callbacks Personalizados</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <div className={classes.codeBlock} style={{ width: '100%' }}>
                            {`<script>
  DzapyTracking.init({
    apiUrl: 'https://api.dzapy.com.br',
    landingPageId: 123,
    debug: true
  });

  // Callback de sucesso
  window.dzapyOnSuccess = function(data, form) {
    console.log('Lead enviado!', data);
    window.location.href = '/obrigado.html';
  };

  // Callback de erro
  window.dzapyOnError = function(error, form) {
    alert('Erro ao enviar. Tente novamente.');
  };
</script>`}
                        </div>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography>WordPress (Sem Plugin)</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <div className={classes.codeBlock} style={{ width: '100%' }}>
                            {`<!-- Adicionar no footer.php do tema -->
<script src="https://dzapy.com.br/tracking/dzapy-tracking.js"></script>
<script>
  DzapyTracking.init({
    apiUrl: 'https://api.dzapy.com.br',
    landingPageId: <?php echo get_option('dzapy_landing_page_id', 1); ?>
  });
</script>`}
                        </div>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography>React/Next.js</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <div className={classes.codeBlock} style={{ width: '100%' }}>
                            {`import { useEffect } from 'react';

function MyForm() {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://dzapy.com.br/tracking/dzapy-tracking.js';
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      window.DzapyTracking.init({
        apiUrl: 'https://api.dzapy.com.br',
        landingPageId: 123
      });
    };

    return () => document.body.removeChild(script);
  }, []);

  return (
    <form data-dzapy="true">
      <input name="name" placeholder="Nome" required />
      <input name="phone" placeholder="WhatsApp" required />
      <button type="submit">Enviar</button>
    </form>
  );
}`}
                        </div>
                    </AccordionDetails>
                </Accordion>

                {/* Testing */}
                <Paper className={classes.paper} style={{ marginTop: 24 }}>
                    <Typography variant="h6" gutterBottom>
                        🧪 Como Testar
                    </Typography>

                    <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
                        1. Verificar se o script carregou:
                    </Typography>
                    <Typography variant="body2" paragraph>
                        Abra o console (F12) e digite: <code>DzapyTracking</code>
                    </Typography>

                    <Typography variant="subtitle2" gutterBottom>
                        2. Testar com UTMs:
                    </Typography>
                    <div className={classes.codeBlock}>
                        https://seusite.com/?utm_source=google&utm_campaign=teste&gclid=ABC123
                    </div>

                    <Typography variant="subtitle2" gutterBottom>
                        3. Verificar UTMs capturadas:
                    </Typography>
                    <Typography variant="body2" paragraph>
                        No console: <code>DzapyTracking.getUTMs()</code>
                    </Typography>
                </Paper>

                {/* Troubleshooting */}
                <Paper className={classes.paper}>
                    <Typography variant="h6" gutterBottom>
                        🐛 Problemas Comuns
                    </Typography>

                    <Box className={classes.warningBox} mb={2}>
                        <Warning style={{ marginRight: 8, color: "#ff9800" }} />
                        <Box>
                            <Typography variant="subtitle2">Script não carrega</Typography>
                            <Typography variant="body2" color="textSecondary">
                                Verifique se a URL do script está correta e se o servidor permite CORS
                            </Typography>
                        </Box>
                    </Box>

                    <Box className={classes.warningBox} mb={2}>
                        <Warning style={{ marginRight: 8, color: "#ff9800" }} />
                        <Box>
                            <Typography variant="subtitle2">UTMs não são capturadas</Typography>
                            <Typography variant="body2" color="textSecondary">
                                Certifique-se que a URL tem os parâmetros UTM e limpe o localStorage
                            </Typography>
                        </Box>
                    </Box>

                    <Box className={classes.warningBox}>
                        <Warning style={{ marginRight: 8, color: "#ff9800" }} />
                        <Box>
                            <Typography variant="subtitle2">Lead não aparece no CRM</Typography>
                            <Typography variant="body2" color="textSecondary">
                                Verifique se apiUrl e landingPageId estão corretos. Veja a resposta da API no Network (F12)
                            </Typography>
                        </Box>
                    </Box>
                </Paper>

                {/* Support */}
                <Box className={classes.contactBox}>
                    <Typography variant="h6" gutterBottom>
                        📞 Suporte
                    </Typography>
                    <Typography variant="body2">
                        <strong>WhatsApp:</strong> (31) 9522-3909
                    </Typography>
                    <Typography variant="body2">
                        <strong>Email:</strong> contato@dzapy.com.br
                    </Typography>
                </Box>
            </Container>
        </div >
    );
};

export default DzapyChatIntegration;
